import React, { useEffect, useMemo, useState } from "react";
import {
    Card,
    Collapse,
    Form,
    Input,
    InputNumber,
    Button,
    Space,
    Typography,
    notification,
    Select,
    Descriptions,
    Tag,
} from "antd";
import { PlusOutlined, DeleteOutlined, SaveOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { getData } from "../../../../../../actions/apiCall";
import CommonView, { CommonViewProps } from "../../../../../../components/CommonView/CommonView";
import { MasterConfigs } from "../../../../../../interfaces/entity";
import { constants } from "../../../../../../constants/common";
import dayjs from 'dayjs';
import MasterComponent from "../../../../../../components/Forms";

const { Panel } = Collapse;
const { Title, Text } = Typography;

interface PTSlab {
    minSalary: number;
    maxSalary: number;
    amount: number;
    februaryAmount?: number;
}

interface PTConfig {
    _id: string;
    state: string;
    financialYear: string;
    deductionBasis: "monthly" | "half-yearly" | "annual";
    calculationBasis: "monthly" | "annual";
    effectiveFrom: string;
    slabs: PTSlab[];
}

const PTSlabManager: React.FC = () => {
    const [selectedListItem, setSelectedListItem] = useState()
    const [configs, setConfigs] = useState<PTConfig[]>([]);
    const dispatch = useDispatch()

    const financialYears = useMemo(() => {
        const count = 4;
        const today = new Date();
        const currentYear = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;

        return Array.from({ length: count }, (_, i) => {
            const from = currentYear - i;
            const to = (from + 1).toString().slice(-2);
            return { name: `${from}-${to}`, value: `${from}-${to}` };
        });
    }, []);

    const config: MasterConfigs = {
        api: () => '/professional-tax',
        getFormColumns: () => [
            {
                label: 'State',
                name: 'state',
                type: 'select',
                selectProps: { showSearch: true },
                children: constants.stateList.map(ele => ({ name: ele, value: ele })),
                rules: [{ required: true, message: 'Field is required' }]
            },
            {
                label: 'Financial Year',
                name: 'financialYear',
                type: 'select',
                children: financialYears,
                rules: [{ required: true, message: 'Field is required' }]
            },
            {
                label: 'Deduction Basis',
                name: 'deductionBasis',
                rules: [{ required: true, message: 'Field is required' }],
                type: 'select',
                children: [
                    { name: 'Monthly', value: 'monthly' },
                    { name: 'Half Yearly', value: 'half-yearly' },
                    { name: 'Annual', value: 'annual' },
                ]
            },
            {
                label: 'Calculation Basis',
                name: 'calculationBasis',
                type: 'select',
                rules: [{ required: true, message: 'Field is required' }],
                children: [
                    { name: 'Monthly', value: 'monthly' },
                    { name: 'Annual', value: 'annual' },
                ]
            },
            {
                label: 'Effective From',
                name: 'effectiveFrom',
                type: 'date',
                rules: [{ required: true, message: 'Field is required' }]
            }
        ],
    }

    const commonViewProps: CommonViewProps = {
        config: config,
        componentConfig: {
            buttonName: 'Add Month',
            title: 'PT Months',
            subTitle: 'PT Months',
            cardTitle: 'PT Months',
            emptyDescription: 'Please select a month',
            renderListItem: (item) => {
                return <>
                    <Title level={5} style={{ lineHeight: 1.2, marginBottom: 0 }}>{item?.state}</Title>
                    <Text type="secondary">{item?.financialYear}</Text>
                </>
            },
            renderComponent: (item) => renderComponent(item),
            selectedItemId: (id) => setSelectedListItem(id)
        },
    }

    const renderComponent = (item) => {
        const slapTableConfig: MasterConfigs = {
            api: () => `/professional-slabs/${item?._id}`,
            getData: item?.slabs,
            isActionTab: true,
            title: 'Slabs',
            getColumns: () => [
                {
                    title: 'Min Salary',
                    dataIndex: 'minSalary',
                    key: 'minSalary',
                    render: (value: number) => <Text>₹{value.toLocaleString()}</Text>,
                },
                {
                    title: 'Max Salary',
                    dataIndex: 'maxSalary',
                    key: 'maxSalary',
                    render: (value: number) => <Text>₹{value.toLocaleString()}</Text>,
                },
                {
                    title: 'Amount',
                    dataIndex: 'amount',
                    key: 'amount',
                    render: (value: number) => <Text strong>₹{value}</Text>,
                },
                {
                    title: 'Half-Yearly',
                    dataIndex: 'halfYearlyAmount',
                    key: 'halfYearlyAmount',
                    render: (value?: any) => value ? <Tag color="blue">₹{value}</Tag> : '--'
                },
                {
                    title: 'Annual',
                    dataIndex: 'annualAmount',
                    key: 'annualAmount',
                    render: (value?: any) => value ? <Tag color="green">₹{value}</Tag> : '--'
                },
                {
                    title: 'Gender',
                    dataIndex: 'gender',
                    key: 'gender',
                    render: (value?: any) => value
                },
                {
                    title: 'Extra Month',
                    dataIndex: 'extraMonth',
                    key: 'extraMonth',
                    render: (value?: any) => value
                },
                {
                    title: 'Extra Amount',
                    dataIndex: 'extraMonthAmount',
                    key: 'extraMonthAmount',
                    render: (value?: any) => value ? <Tag color="green">₹{value}</Tag> : '--'
                }
            ],
            getFormColumns: () => [
                {
                    label: 'Min Salary',
                    name: 'minSalary',
                    type: 'text'
                },
                {
                    label: 'Max Salary',
                    name: 'maxSalary',
                    type: 'text'
                },
                {
                    label: 'Gender',
                    name: 'gender',
                    type: 'select',
                    children: [{ name: 'All', value: 'All' }, { name: 'Male', value: 'Male' }, { name: 'Female', value: 'Female' }, { name: 'Other', value: 'Other' }]
                },
                {
                    label: 'Amount',
                    name: 'amount',
                    type: 'text'
                },
                {
                    label: 'Extra Month',
                    name: 'extraMonth',
                    type: 'select',
                    children: constants.ALL_MONTS.map(ele => ({ name: ele, value: ele }))
                },
                {
                    label: 'Extra Month Amount',
                    name: 'extraMonthAmount',
                    type: 'text'
                },
                {
                    label: 'Half-Yearly',
                    name: 'halfYearlyAmount',
                    type: 'text'
                },
                {
                    label: 'Annual',
                    name: 'annualAmount',
                    type: 'text'
                },
            ],
            actions: [{ name: 'Add', actionType: 'Add' }]
        }


        return <div>
            <Title level={5} style={{ marginBottom: 16 }}>
                Financial Year: {item.financialYear}
            </Title>
            <Descriptions column={2} bordered size="small">
                <Descriptions.Item label="State">{item.state}</Descriptions.Item>
                <Descriptions.Item label="Financial Year">{item.financialYear}</Descriptions.Item>
                <Descriptions.Item label="Deduction Basis">
                    <Tag color="blue">{item.deductionBasis}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Calculation Basis">
                    <Tag color="purple">{item.calculationBasis}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Effective From">
                    {dayjs(item.effectiveFrom).format("DD MMM YYYY")}
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                    <Tag color={item.active ? "green" : "red"}>{item.active ? "Active" : "Inactive"}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Created At">
                    {dayjs(item.createdAt).format("DD MMM YYYY HH:mm")}
                </Descriptions.Item>
                <Descriptions.Item label="Updated At">
                    {dayjs(item.updatedAt).format("DD MMM YYYY HH:mm")}
                </Descriptions.Item>
            </Descriptions>
            <div className="mt-5">
                <MasterComponent config={slapTableConfig} />
            </div>
        </div>
    }

    return (
        <CommonView {...commonViewProps} />
    );
};

export default PTSlabManager;
